import { adminAPI } from '@/services/api'
import { options } from '@/admin/labels'
import { countryOptions } from '@/constants/subRegionCountries'

const text = (key, label, options = {}) => ({ key, label, type: 'text', ...options })
const number = (key, label, options = {}) => ({ key, label, type: 'number', ...options })
const select = (key, label, options, extra = {}) => ({ key, label, type: 'select', options, ...extra })
const textarea = (key, label, options = {}) => ({ key, label, type: 'textarea', ...options })
const boolean = (key, label, options = {}) => ({ key, label, type: 'boolean', ...options })
const json = (key, label, options = {}) => ({ key, label, type: 'json', ...options })
// Textarea edited as one value per line, stored as an array of strings.
const lines = (key, label, options = {}) => ({ key, label, type: 'lines', ...options })
// Repeatable list of objects; `subfields` reuses the field helpers above.
const repeater = (key, label, subfields, options = {}) => ({ key, label, type: 'repeater', subfields, ...options })

const guideCategoryOptions = [
  { value: 'financement', label: 'Financement' },
  { value: 'administratif', label: 'Administratif' },
  { value: 'scolarite', label: 'Scolarité' },
  { value: 'orientation', label: 'Orientation' },
  { value: 'autre', label: 'Autre' },
]
const besoinCategoryOptions = [
  { value: 'fees', label: 'Frais de scolarité' },
  { value: 'supplies', label: 'Fournitures' },
  { value: 'uniform', label: 'Uniforme' },
  { value: 'transport', label: 'Transport' },
  { value: 'documents', label: 'Documents' },
  { value: 'other', label: 'Autre' },
]

export const resources = {
  admins: {
    key: 'admins',
    label: 'Admins',
    singular: 'admin',
    path: 'admins',
    icon: 'shield-alt',
    api: adminAPI.admins,
    superAdminOnly: true,
    responseKey: 'admin',
    titleField: item => item.name || item.email,
    statusField: 'status',
    columns: [
      { key: 'name', label: 'Nom' },
      { key: 'email', label: 'Email' },
      { key: 'role', label: 'Rôle', badge: true },
      { key: 'status', label: 'Statut', badge: true },
      { key: 'lastLoginAt', label: 'Dernière connexion', format: 'dateTime' },
    ],
    fields: [
      text('name', 'Nom'),
      text('email', 'Email', { required: true, inputType: 'email' }),
      select('role', 'Rôle', options(['super_admin', 'admin', 'support'])),
      select('status', 'Statut', options(['active', 'inactive'])),
      text('password', 'Mot de passe', { createOnly: true, requiredOnCreate: true, inputType: 'password', autocomplete: 'new-password' }),
    ],
    detail: ['name', 'email', 'role', 'status', 'lastLoginAt', 'createdAt', 'updatedAt'],
  },
  mobileUsers: {
    key: 'mobileUsers',
    label: 'Utilisateurs',
    singular: 'utilisateur',
    path: 'mobile-users',
    icon: 'users',
    api: adminAPI.mobileUsers,
    titleField: item => item.name || item.phone,
    statusField: 'status',
    columns: [
      { key: 'name', label: 'Nom' },
      { key: 'accountType', label: 'Type', badge: true },
      { key: 'phone', label: 'Téléphone' },
      { key: 'city', label: 'Ville' },
      { key: 'country', label: 'Pays' },
      { key: 'status', label: 'Statut', badge: true },
      { key: 'lastLoginAt', label: 'Connexion', format: 'dateTime' },
    ],
    fields: [
      text('phone', 'Téléphone', { required: true }),
      text('dialCode', 'Indicatif'),
      text('name', 'Nom'),
      select('accountType', 'Type de compte', options(['parent', 'learner'])),
      text('role', 'Rôle'),
      text('city', 'Ville'),
      text('country', 'Pays'),
      text('userCode', 'Code utilisateur'),
      select('status', 'Statut', options(['active', 'inactive'])),
      json('fcmTokens', 'Jetons push (FCM)'),
      json('state', 'État app'),
    ],
    detail: ['phone', 'dialCode', 'name', 'accountType', 'role', 'city', 'country', 'userCode', 'status', 'fcmTokens', 'lastLoginAt', 'createdAt', 'updatedAt'],
  },
  children: {
    key: 'children',
    label: 'Enfants',
    singular: 'enfant',
    path: 'children',
    icon: 'child',
    api: adminAPI.children,
    titleField: item => item.firstName || 'Profil enfant',
    statusField: 'status',
    columns: [
      { key: 'firstName', label: 'Prénom' },
      { key: 'age', label: 'Âge' },
      { key: 'classLevel', label: 'Classe' },
      { key: 'city', label: 'Ville' },
      { key: 'onlineEnabled', label: 'Digitale', badge: true },
      { key: 'logecoEnabled', label: 'Connectée', badge: true },
      { key: 'status', label: 'Statut', badge: true },
    ],
    fields: [
      text('userId', 'Parent', { required: true }),
      text('firstName', 'Prénom'),
      number('age', 'Âge'),
      text('gender', 'Genre'),
      text('classLevel', 'Classe'),
      text('city', 'Ville'),
      text('currentSchool', 'École actuelle'),
      text('targetSchool', 'École cible'),
      text('fieldOfStudy', 'Filière / domaine'),
      textarea('goal', 'Objectif / projet'),
      text('avatar', 'Avatar URL'),
      text('publicShareToken', 'Lien public (token) — vider pour révoquer'),
      select('status', 'Statut', options(['active', 'archived'])),
      textarea('notes', 'Notes'),
    ],
    detail: ['firstName', 'age', 'gender', 'classLevel', 'city', 'currentSchool', 'targetSchool', 'fieldOfStudy', 'goal', 'publicShareToken', 'status', 'createdAt', 'updatedAt'],
  },
  actions: {
    key: 'actions',
    label: 'Besoins',
    singular: 'besoin',
    path: 'actions',
    icon: 'clipboard-check',
    api: adminAPI.actions,
    hiddenFromMenu: true,
    titleField: item => item.title || 'Besoin',
    statusField: 'statusKey',
    columns: [
      { key: 'title', label: 'Titre' },
      { key: 'sectionKey', label: 'Section', badge: true },
      { key: 'categoryKey', label: 'Catégorie', badge: true },
      { key: 'statusKey', label: 'Statut', badge: true },
      { key: 'dueDate', label: 'Échéance' },
      { key: 'paidAmount', label: 'Payé', format: 'money' },
    ],
    fields: [
      text('userId', 'Parent', { required: true }),
      text('childId', 'Enfant'),
      text('title', 'Titre'),
      text('sectionKey', 'Section'),
      select('intent', 'Intention', options(['financer', 'trouver', 'aide'])),
      text('categoryKey', 'Catégorie'),
      text('statusKey', 'Statut'),
      text('assignee', 'Assigné à'),
      text('dueDate', 'Échéance', { inputType: 'date' }),
      number('estimatedAmount', 'Montant estimé'),
      number('paidAmount', 'Montant payé'),
      select('sharedCharge', 'Charge partagée', options([true, false])),
      textarea('notes', 'Notes'),
      json('participants', 'Participants'),
      json('paymentHistory', 'Historique paiement'),
    ],
    detail: ['title', 'categoryKey', 'statusKey', 'assignee', 'dueDate', 'estimatedAmount', 'paidAmount', 'sharedCharge', 'notes', 'createdAt', 'updatedAt'],
  },
  documents: {
    key: 'documents',
    label: 'Documents',
    singular: 'document',
    path: 'documents',
    icon: 'file-alt',
    api: adminAPI.documents,
    hiddenFromMenu: true,
    titleField: item => item.title || item.fileName || 'Document',
    statusField: 'typeKey',
    columns: [
      { key: 'title', label: 'Titre' },
      { key: 'typeKey', label: 'Type', badge: true },
      { key: 'date', label: 'Date' },
      { key: 'amount', label: 'Montant', format: 'money' },
      { key: 'fileName', label: 'Fichier' },
    ],
    fields: [
      text('userId', 'Parent', { required: true }),
      text('childId', 'Enfant'),
      text('plannerActionId', 'Action liée'),
      text('title', 'Titre'),
      text('typeKey', 'Type'),
      text('date', 'Date', { inputType: 'date' }),
      text('sourceKey', 'Origine'),
      number('amount', 'Montant'),
      text('fileName', 'Nom du fichier'),
      text('fileUrl', 'Lien du fichier'),
      text('mimeType', 'Format du fichier'),
      textarea('note', 'Note'),
    ],
    detail: ['title', 'typeKey', 'date', 'sourceKey', 'amount', 'fileName', 'fileUrl', 'mimeType', 'note', 'createdAt', 'updatedAt'],
  },
  needContributions: {
    key: 'needContributions',
    label: 'Propositions de soutien',
    singular: 'proposition',
    path: 'need-contributions',
    icon: 'hand-holding-heart',
    api: adminAPI.needContributions,
    titleField: item => `${item.supporterName || 'Soutien'} · ${item.type || ''}`,
    statusField: 'status',
    columns: [
      { key: 'supporterName', label: 'Soutien' },
      { key: 'type', label: 'Type', badge: true },
      { key: 'needTitle', label: 'Besoin' },
      { key: 'amount', label: 'Montant', format: 'money' },
      { key: 'status', label: 'Statut', badge: true },
      { key: 'createdAt', label: 'Reçu le', format: 'date' },
    ],
    fields: [
      select('status', 'Statut', options(['new', 'accepted', 'declined'])),
    ],
    detail: ['supporterName', 'supporterContact', 'type', 'needTitle', 'amount', 'message', 'status', 'contactRevealed', 'sourceIp', 'createdAt', 'updatedAt'],
  },
  shares: {
    key: 'shares',
    label: 'Partages',
    singular: 'partage',
    path: 'shares',
    icon: 'share-alt',
    api: adminAPI.shares,
    hiddenFromMenu: true,
    titleField: item => item.invitedUserCode || item.category || 'Partage',
    statusField: 'status',
    columns: [
      { key: 'invitedUserCode', label: 'Code invité' },
      { key: 'category', label: 'Catégorie', badge: true },
      { key: 'actionScope', label: 'Portée' },
      { key: 'status', label: 'Statut', badge: true },
    ],
    fields: [
      text('ownerUserId', 'Propriétaire', { required: true }),
      text('invitedUserId', 'Invité'),
      text('childId', 'Enfant', { required: true }),
      text('invitedUserCode', 'Code invité'),
      select('category', 'Catégorie', options(['co_parent', 'family', 'public'])),
      select('actionScope', 'Portée', options(['all_actions', 'selected_actions'])),
      select('status', 'Statut', options(['pending', 'active', 'revoked'])),
      json('selectedActionIds', 'Actions sélectionnées'),
    ],
    detail: ['invitedUserCode', 'category', 'actionScope', 'status', 'createdAt', 'updatedAt'],
  },
  schools: {
    key: 'schools',
    label: 'Écoles',
    singular: 'école',
    path: 'schools',
    icon: 'school',
    api: adminAPI.schools,
    titleField: item => item.name || 'École',
    statusField: 'status',
    columns: [
      { key: 'name', label: 'Nom' },
      { key: 'city', label: 'Ville' },
      { key: 'status', label: 'Statut', badge: true },
      { key: 'budgetMin', label: 'Budget min', format: 'money' },
      { key: 'budgetMax', label: 'Budget max', format: 'money' },
      { key: 'favoriteCount', label: 'Favoris' },
    ],
    fields: [
      text('name', 'Nom', { required: true }),
      text('city', 'Ville'),
      select('country', 'Pays', countryOptions),
      json('classLevels', 'Niveaux'),
      number('budgetMin', 'Budget min'),
      number('budgetMax', 'Budget max'),
      textarea('description', 'Description'),
      json('features', 'Atouts'),
      json('requiredDocuments', 'Documents requis'),
      text('contact', 'Téléphone'),
      text('whatsapp', 'WhatsApp (numéro)'),
      text('email', 'Email'),
      text('website', 'Site web'),
      text('logoUrl', 'Logo (URL)'),
      text('imageUrl', 'Image (URL)'),
      json('gallery', 'Galerie'),
      select('onlineEnabled', 'École digitale / en ligne', [{ value: true, label: 'Oui' }, { value: false, label: 'Non' }], { default: false }),
      select('logecoEnabled', 'Connexion Logeco classique', [{ value: true, label: 'Oui' }, { value: false, label: 'Non' }], { default: false }),
      text('slug', 'Slug public'),
      text('shortCode', 'Code public / QR'),
      text('sourceId', 'ID source API école'),
      select('status', 'Statut', options(['draft', 'published', 'archived'])),
      number('favoriteCount', 'Favoris'),
    ],
    detail: ['name', 'city', 'country', 'onlineEnabled', 'logecoEnabled', 'sourceId', 'slug', 'shortCode', 'classLevels', 'budgetMin', 'budgetMax', 'contact', 'website', 'status', 'favoriteCount', 'createdAt', 'updatedAt'],
  },
  opportunities: {
    key: 'opportunities',
    label: 'Opportunités',
    singular: 'opportunité',
    path: 'opportunities',
    icon: 'bolt',
    api: adminAPI.opportunities,
    titleField: item => item.title || 'Opportunité',
    statusField: 'status',
    columns: [
      { key: 'title', label: 'Titre' },
      { key: 'typeKey', label: 'Type', badge: true },
      { key: 'city', label: 'Ville' },
      { key: 'deadline', label: 'Échéance' },
      { key: 'status', label: 'Statut', badge: true },
    ],
    fields: [
      text('title', 'Titre', { required: true }),
      text('city', 'Ville'),
      select('country', 'Pays', countryOptions),
      json('classLevels', 'Niveaux'),
      text('typeKey', 'Type'),
      number('budgetMin', 'Budget min'),
      number('budgetMax', 'Budget max'),
      text('deadline', 'Échéance', { inputType: 'date' }),
      textarea('description', 'Description'),
      text('contact', 'Téléphone'),
      text('whatsapp', 'WhatsApp (numéro)'),
      text('imageUrl', 'Image (URL)'),
      text('linkUrl', 'Lien externe (CTA)'),
      text('ctaLabel', 'Libellé du bouton (optionnel)'),
      select('status', 'Statut', options(['draft', 'published', 'archived'])),
      number('favoriteCount', 'Favoris'),
    ],
    detail: ['title', 'city', 'country', 'classLevels', 'typeKey', 'budgetMin', 'budgetMax', 'deadline', 'linkUrl', 'status', 'favoriteCount', 'createdAt', 'updatedAt'],
  },
  opportunityTypes: {
    key: 'opportunityTypes',
    label: 'Catégories opportunités',
    singular: 'catégorie',
    path: 'opportunity-types',
    icon: 'list',
    api: adminAPI.opportunityTypes,
    titleField: item => item.label || item.key,
    columns: [
      { key: 'order', label: 'Ordre' },
      { key: 'label', label: 'Libellé' },
      { key: 'key', label: 'Clé (slug)' },
      { key: 'active', label: 'Actif', badge: true },
    ],
    fields: [
      text('key', 'Clé (slug, ex. bourse)', { required: true }),
      text('label', 'Libellé', { required: true }),
      text('icon', 'Icône (clé, ex. scholarship)'),
      number('order', 'Ordre d’affichage'),
      select('active', 'Actif', [{ value: true, label: 'Oui' }, { value: false, label: 'Non' }], { default: true }),
    ],
    detail: ['key', 'label', 'icon', 'order', 'active', 'createdAt', 'updatedAt'],
  },
  notifications: {
    key: 'notifications',
    label: 'Notifications',
    singular: 'notification',
    path: 'notifications',
    icon: 'bell',
    api: adminAPI.notifications,
    titleField: item => item.title || 'Notification',
    statusField: 'target',
    columns: [
      { key: 'title', label: 'Titre' },
      { key: 'typeKey', label: 'Type', badge: true },
      { key: 'target', label: 'Cible', badge: true },
      { key: 'sentAt', label: 'Envoyée', format: 'dateTime' },
    ],
    fields: [
      text('userId', 'Parent'),
      text('title', 'Titre', { required: true }),
      textarea('body', 'Message'),
      text('typeKey', 'Type'),
      select('target', 'Cible', options(['single', 'segment', 'broadcast'])),
      json('metadata', 'Métadonnées'),
    ],
    detail: ['title', 'body', 'typeKey', 'target', 'sentAt', 'createdAt', 'updatedAt'],
  },
  schoolLifeSections: {
    key: 'schoolLifeSections',
    label: 'Suggestions du cartable',
    singular: 'groupe de suggestions',
    path: 'school-life-sections',
    icon: 'calendar-alt',
    api: adminAPI.schoolLifeSections,
    titleField: item => item.label || item.key,
    statusField: 'status',
    columns: [
      { key: 'order', label: 'Ordre' },
      { key: 'label', label: 'Nom' },
      { key: 'key', label: 'Clé' },
      { key: 'status', label: 'Statut', badge: true },
    ],
    fields: [
      text('key', 'Clé (slug)', { required: true }),
      text('label', 'Nom'),
      textarea('description', 'Description'),
      text('icon', 'Icône (clé)'),
      json('accountTypes', 'Profils (parent/learner)'),
      number('order', 'Ordre d’affichage'),
      number('seasonStartMonth', 'Mois début saison (1-12)'),
      number('seasonEndMonth', 'Mois fin saison (1-12)'),
      json('templates', 'Modèles d’actions (titre, categoryKey, estimatedAmount, dueDate)'),
      select('status', 'Statut', options(['active', 'archived'])),
    ],
    detail: ['key', 'label', 'description', 'icon', 'accountTypes', 'order', 'seasonStartMonth', 'seasonEndMonth', 'status', 'createdAt', 'updatedAt'],
  },
  helpArticles: {
    key: 'helpArticles',
    label: 'Centre d’aide',
    singular: 'article d’aide',
    path: 'help-articles',
    icon: 'info-circle',
    api: adminAPI.helpArticles,
    titleField: item => item.title || item.key,
    statusField: 'status',
    columns: [
      { key: 'order', label: 'Ordre' },
      { key: 'title', label: 'Titre (FR)' },
      { key: 'key', label: 'Clé' },
      { key: 'status', label: 'Statut', badge: true },
    ],
    fields: [
      text('key', 'Clé (slug)', { required: true }),
      text('title', 'Titre (FR)', { required: true }),
      textarea('body', 'Contenu (FR)'),
      text('titleEn', 'Titre (EN, optionnel)'),
      textarea('bodyEn', 'Contenu (EN, optionnel)'),
      text('imageUrl', 'Image (URL, optionnel)'),
      text('videoUrl', 'Vidéo YouTube (lien, optionnel)'),
      text('icon', 'Icône (clé)'),
      number('order', 'Ordre d’affichage'),
      select('status', 'Statut', options(['active', 'archived'])),
    ],
    detail: ['key', 'title', 'body', 'titleEn', 'bodyEn', 'imageUrl', 'videoUrl', 'icon', 'order', 'status', 'createdAt', 'updatedAt'],
  },
  guides: {
    key: 'guides',
    label: 'Guides',
    singular: 'guide',
    path: 'guides',
    icon: 'book-open',
    api: adminAPI.guides,
    titleField: item => item.title || 'Guide',
    statusField: 'status',
    columns: [
      { key: 'order', label: 'Ordre' },
      { key: 'title', label: 'Titre' },
      { key: 'category', label: 'Catégorie', badge: true },
      { key: 'version', label: 'Version' },
      { key: 'active', label: 'Activé', badge: true },
      { key: 'status', label: 'Statut', badge: true },
    ],
    fields: [
      text('title', 'Titre', { required: true }),
      select('category', 'Catégorie', guideCategoryOptions),
      textarea('summary', 'Description / promesse'),
      text('icon', 'Icône (emoji ou clé)'),
      number('order', 'Ordre d’affichage'),
      number('version', 'Version (incrémentez pour notifier les profils)', { default: 1 }),
      select('status', 'Statut', options(['draft', 'published', 'archived'])),
      select('active', 'Activé', [{ value: true, label: 'Oui' }, { value: false, label: 'Non' }], { default: true }),
      repeater('eligibility', 'Critères d’éligibilité', [
        text('label', 'Critère'),
        text('detail', 'Précision'),
      ]),
      repeater('documents', 'Documents requis', [
        text('name', 'Document'),
        text('howToGet', 'Comment l’obtenir'),
      ]),
      repeater('steps', 'Étapes', [
        text('title', 'Titre de l’étape'),
        textarea('description', 'Description'),
        lines('todos', 'Tâches (une par ligne)'),
      ]),
      repeater('besoins', 'Besoins', [
        text('label', 'Besoin'),
        select('categoryKey', 'Catégorie', besoinCategoryOptions),
        number('estimatedAmount', 'Montant estimé'),
      ]),
    ],
    detail: ['title', 'category', 'summary', 'icon', 'order', 'version', 'status', 'active', 'eligibility', 'documents', 'steps', 'besoins', 'createdAt', 'updatedAt'],
  },
  appConfig: {
    key: 'appConfig',
    label: 'Configuration App',
    singular: 'configuration',
    path: 'app-config',
    icon: 'mobile-alt',
    api: adminAPI.appConfig,
    titleField: item => `Config ${item.key || 'mobile'}`,
    columns: [
      { key: 'key', label: 'Clé' },
      { key: 'androidLatestBuild', label: 'Android (dernier)' },
      { key: 'iosLatestBuild', label: 'iOS (dernier)' },
      { key: 'updatedAt', label: 'Modifié', format: 'dateTime' },
    ],
    fields: [
      text('key', 'Clé', { required: true, default: 'mobile' }),
      number('androidMinBuild', 'Android — build minimum (forcé en dessous)'),
      number('androidLatestBuild', 'Android — dernier build (proposé en dessous)'),
      text('androidLatestVersionName', 'Android — nom de version (ex. 2.3.0)'),
      text('androidStoreUrl', 'Android — URL Play Store'),
      number('iosMinBuild', 'iOS — build minimum (forcé en dessous)'),
      number('iosLatestBuild', 'iOS — dernier build (proposé en dessous)'),
      text('iosLatestVersionName', 'iOS — nom de version (ex. 2.3.0)'),
      text('iosStoreUrl', 'iOS — URL App Store'),
      textarea('updateMessage', 'Message du popup'),
      textarea('shareMessage', 'Message de partage de l’app (vide = défaut)'),
      boolean('featureSchools', 'Activer la section Écoles', { default: true }),
      boolean('featureAccompaniment', 'Activer l’Accompagnement (partenaires)', { default: true }),
      boolean('featureApprendre', 'Activer Apprendre', { default: true }),
      boolean('featureDigitale', 'Activer les écoles digitales', { default: true }),
      boolean('explorerHideConnect', 'Masquer Connecter mon école', { default: false }),
      boolean('explorerHideOnline', 'Masquer Écoles digitales', { default: false }),
      boolean('explorerHideParcours', 'Masquer Parcours', { default: false }),
      boolean('explorerHideOpportunities', 'Masquer Opportunités', { default: false }),
      boolean('explorerHideGuides', 'Masquer Guides', { default: false }),
      boolean('explorerHideSchools', 'Masquer Écoles classiques', { default: false }),
    ],
    detail: ['key', 'androidMinBuild', 'androidLatestBuild', 'androidLatestVersionName', 'androidStoreUrl', 'iosMinBuild', 'iosLatestBuild', 'iosLatestVersionName', 'iosStoreUrl', 'updateMessage', 'shareMessage', 'featureSchools', 'featureAccompaniment', 'featureApprendre', 'featureDigitale', 'explorerHideConnect', 'explorerHideOnline', 'explorerHideParcours', 'explorerHideOpportunities', 'explorerHideGuides', 'explorerHideSchools', 'updatedAt'],
  },
  auditLogs: {
    key: 'auditLogs',
    label: 'Audit',
    singular: 'log',
    path: 'audit-logs',
    icon: 'database',
    api: adminAPI.auditLogsResource,
    superAdminOnly: true,
    readOnly: true,
    titleField: item => item.action || 'Événement',
    statusField: 'actorType',
    columns: [
      { key: 'action', label: 'Action' },
      { key: 'actorEmail', label: 'Acteur' },
      { key: 'actorType', label: 'Type', badge: true },
      { key: 'resourceType', label: 'Ressource' },
      { key: 'createdAt', label: 'Date', format: 'dateTime' },
    ],
    fields: [],
    detail: ['actorType', 'actorEmail', 'action', 'resourceType', 'resourceId', 'ip', 'createdAt'],
  },
}

export const resourceList = Object.values(resources)

export function getResource(key) {
  return resources[key]
}
